// UserProfileCoverService.js

// Firebase
import { doc, getDoc } from 'firebase/firestore';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';

import { collections, db, storage } from 'firebaseConfig';

// Services
import { updateNode } from 'services/nodes/NodeWriteService';
import { prepareBaseUploadIntent } from 'services/storage/StorageUploadIntentService';
import { assertBaseUploadAllowed } from 'services/bases/BaseUploadPolicyService';

const MAX_SOURCE_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_OUTPUT_DIMENSION = 1024;
const OUTPUT_MIME_TYPE = 'image/jpeg';
const OUTPUT_QUALITY = 0.9;

function normalizeStoragePath(value) {
    return String(value || '').trim().replace(/^\/+/, '');
}

async function resolveBaseKeyForNode(nodeKey) {
    const normalizedNodeKey = String(nodeKey || '').trim();
    if (!normalizedNodeKey) {
        return null;
    }

    const nodeSnap = await getDoc(doc(db, collections.nodes, normalizedNodeKey));
    if (!nodeSnap.exists()) {
        return null;
    }

    return String(nodeSnap.data()?.baseKey || '').trim() || null;
}

async function deleteStorageObjectIfPresent(storagePath) {
    const normalizedPath = normalizeStoragePath(storagePath);
    if (!normalizedPath) {
        return;
    }

    try {
        await deleteObject(ref(storage, normalizedPath));
    } catch (error) {
        if (error?.code === 'storage/object-not-found') {
            return;
        }
    }
}

function sanitizeFileStem(name) {
    const stem = String(name || 'cover')
        .replace(/\.[^/.]+$/, '')
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);
    return stem || 'cover';
}

function fileToImage(file) {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };

        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Invalid image file.'));
        };

        image.src = objectUrl;
    });
}

function canvasToBlob(canvas, mimeType, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                    return;
                }
                reject(new Error('Failed to process image.'));
            },
            mimeType,
            quality
        );
    });
}

async function buildSquareCoverBlob(file) {
    if (!file || !file.type || !file.type.startsWith('image/')) {
        throw new Error('Please select an image file.');
    }

    if (file.size > MAX_SOURCE_FILE_SIZE_BYTES) {
        throw new Error('Image must be 20MB or smaller.');
    }

    const image = await fileToImage(file);

    const sourceSize = Math.min(image.naturalWidth, image.naturalHeight);
    if (!sourceSize || sourceSize <= 0) {
        throw new Error('Unable to read image dimensions.');
    }

    const cropX = Math.floor((image.naturalWidth - sourceSize) / 2);
    const cropY = Math.floor((image.naturalHeight - sourceSize) / 2);
    const targetSize = Math.min(sourceSize, MAX_OUTPUT_DIMENSION);

    const canvas = document.createElement('canvas');
    canvas.width = targetSize;
    canvas.height = targetSize;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Failed to initialize image processor.');
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
        image,
        cropX,
        cropY,
        sourceSize,
        sourceSize,
        0,
        0,
        targetSize,
        targetSize
    );

    return canvasToBlob(canvas, OUTPUT_MIME_TYPE, OUTPUT_QUALITY);
}

export async function uploadUserProfileCover({ userNodeKey, file, previousCoverPath = null, actor = null }) {
    const normalizedUserNodeKey = String(userNodeKey || '').trim();
    if (!normalizedUserNodeKey) {
        throw new Error('User node key is required.');
    }

    const baseKey = await resolveBaseKeyForNode(normalizedUserNodeKey);
    if (!baseKey) {
        throw new Error('Base key is required for profile photo uploads.');
    }

    const processedBlob = await buildSquareCoverBlob(file);
    const fileStem = sanitizeFileStem(file?.name);
    const storagePath = `nodes/${normalizedUserNodeKey}/cover/cover-${Date.now()}-${fileStem}.jpg`;
    const normalizedPreviousPath = normalizeStoragePath(previousCoverPath);
    const expectedPrefix = `nodes/${normalizedUserNodeKey}/cover/`;

    await assertBaseUploadAllowed({
        baseKey,
        fileName: file?.name || 'profile photo',
        fileSizeBytes: processedBlob.size,
    });

    const uploadIntent = await prepareBaseUploadIntent({
        baseKey,
        storagePath,
        fileSizeBytes: processedBlob.size,
        contentType: OUTPUT_MIME_TYPE,
        uploadKind: 'user-profile-cover',
        nodeKey: normalizedUserNodeKey,
        itemKey: normalizedUserNodeKey,
    });
    const intentId = String(uploadIntent?.intentId || '').trim();

    const objectRef = ref(storage, storagePath);
    await uploadBytes(objectRef, processedBlob, {
        contentType: OUTPUT_MIME_TYPE,
        cacheControl: 'public, max-age=3600',
        customMetadata: {
            intentId,
            nodeKey: normalizedUserNodeKey,
            uploader: normalizedUserNodeKey,
            baseKey,
        },
    });

    await updateNode(normalizedUserNodeKey, {
        coverUrl: storagePath,
        coverStoragePath: storagePath,
    }, { actor });

    if (normalizedPreviousPath.startsWith(expectedPrefix) && normalizedPreviousPath !== storagePath) {
        await deleteStorageObjectIfPresent(normalizedPreviousPath);
    }

    return storagePath;
}

export async function clearUserProfileCover({ userNodeKey, previousCoverPath = null, actor = null }) {
    const normalizedUserNodeKey = String(userNodeKey || '').trim();
    if (!normalizedUserNodeKey) {
        throw new Error('User node key is required.');
    }

    const normalizedPreviousPath = normalizeStoragePath(previousCoverPath);
    const expectedPrefix = `nodes/${normalizedUserNodeKey}/cover/`;

    await updateNode(normalizedUserNodeKey, {
        coverUrl: null,
        coverStoragePath: null,
    }, { actor });

    if (normalizedPreviousPath.startsWith(expectedPrefix)) {
        await deleteStorageObjectIfPresent(normalizedPreviousPath);
    }
}